import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogOut, Save } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const formatErr = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || "").join(" ");
  return "Sign in failed. Please try again.";
};

const AdminControls = () => {
  const { admin, login, logout, loginOpen, setLoginOpen, dirty, save } = useSite();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      setLoginOpen(false);
      setEmail("");
      setPassword("");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="border-[#EADDCF] bg-[#F5F2EA] sm:max-w-sm" data-testid="admin-login-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">Owner login</DialogTitle>
            <DialogDescription className="text-sm text-[#5C564D]">
              Sign in to edit the words on this site.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-none border-[#EADDCF] bg-white"
              />
            </div>
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-none border-[#EADDCF] bg-white"
              />
            </div>
            {error && (
              <p className="text-sm text-[#DA121A]" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full rounded-none bg-[#1A1713] text-[#F5F2EA] transition-colors hover:bg-[#1A1713]/90"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      {admin && (
        <div
          className="fixed bottom-5 right-5 z-[100] flex items-center gap-2 border border-[#1A1713]/15 bg-white/90 px-3 py-2 backdrop-blur-md"
          data-testid="admin-toolbar"
        >
          <span className="mr-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-[#5C564D]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#078930]" aria-hidden="true" />
            Admin mode — click any text to edit
          </span>
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty}
            data-testid="admin-save-btn"
            className="rounded-none bg-[#1A1713] text-[#F5F2EA] transition-colors hover:bg-[#1A1713]/90"
          >
            <Save className="mr-1.5 h-3.5 w-3.5" />
            {dirty ? "Save changes" : "Saved"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="rounded-none border-[#1A1713]/20"
          >
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </>
  );
};

export default AdminControls;
